import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  addServiceTicketTimelineEntry,
  getServiceTicketDetails,
  listClientServiceTicketsByClient,
  updateServiceTicketStatus,
} from '../../api/backend';

const STATUS_OPTIONS = [
  { value: 'abierto', label: 'Abierto' },
  { value: 'en_proceso', label: 'En proceso' },
  { value: 'en_espera_cliente', label: 'En espera del cliente' },
  { value: 'resuelto', label: 'Resuelto' },
  { value: 'cerrado', label: 'Cerrado' },
];

const PRIORITY_STYLES = {
  baja: 'bg-gray-100 text-gray-700',
  media: 'bg-blue-100 text-blue-700',
  alta: 'bg-amber-100 text-amber-700',
  critica: 'bg-red-100 text-red-700',
};

const formatDateTime = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleString('es-PR', {
        hour: '2-digit',
        minute: '2-digit',
        month: 'short',
        day: 'numeric',
      })
    : '—';
};

const AdminClientService = () => {
  const { clientId } = useParams();
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [note, setNote] = useState('');
  const OPEN_STATUSES = ['abierto', 'en_proceso', 'en_espera_cliente'];

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const response = await listClientServiceTicketsByClient(clientId);
        setTickets(response.tickets || []);
      } catch (err) {
        console.error('❌ Error cargando tickets del cliente:', err);
      }
    };
    fetchTickets();
  }, [clientId]);

  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);
    try {
      const response = await getServiceTicketDetails({ clientId, ticketId: ticket.id });
      setSelectedTicket(response.ticket);
      setTimeline(response.timeline || []);
    } catch (err) {
      console.error('❌ Error cargando timeline:', err);
    }
  };

  const addEntry = async () => {
    if (!selectedTicket || !note.trim()) return;
    try {
      const response = await addServiceTicketTimelineEntry({
        clientId,
        ticketId: selectedTicket.id,
        summary: note.trim(),
      });
      setNote('');
      setTimeline(response.timeline || []);
      const refreshed = await getServiceTicketDetails({ clientId, ticketId: selectedTicket.id });
      setSelectedTicket(refreshed.ticket);
      setTickets((prev) => prev.map((t) => (t.id === refreshed.ticket.id ? refreshed.ticket : t)));
    } catch (err) {
      console.error('❌ Error agregando nota:', err);
    }
  };

  const changeStatus = async (status) => {
    if (!selectedTicket) return;
    try {
      const updatedTicket = await updateServiceTicketStatus({
        clientId,
        ticketId: selectedTicket.id,
        status,
      });
      setTickets((prev) => prev.map((t) => (t.id === updatedTicket.id ? updatedTicket : t)));
      setSelectedTicket(updatedTicket);
    } catch (err) {
      console.error('❌ Error actualizando estado:', err);
    }
  };

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-900">Servicio del cliente</h3>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow border">
          <ul className="divide-y divide-gray-100 max-h-[60vh] overflow-y-auto">
            {tickets
              .filter((ticket) => OPEN_STATUSES.includes((ticket.status || '').toLowerCase()))
              .map((ticket) => (
              <li
                key={ticket.id}
                className={`px-4 py-3 cursor-pointer ${
                  selectedTicket?.id === ticket.id ? 'bg-orange-50' : 'hover:bg-gray-50'
                }`}
                onClick={() => openTicket(ticket)}
              >
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-gray-900">{ticket.title}</p>
                  <span className={`text-xs px-2 py-1 rounded-full ${PRIORITY_STYLES[ticket.priority] || 'bg-gray-100'}`}>
                    {ticket.priority?.toUpperCase()}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  {STATUS_OPTIONS.find((s) => s.value === ticket.status)?.label || ticket.status}
                </p>
              </li>
            ))}
            {tickets.filter((ticket) => OPEN_STATUSES.includes((ticket.status || '').toLowerCase())).length === 0 && (
              <li className="px-4 py-6 text-sm text-gray-500">Sin tickets registrados.</li>
            )}
          </ul>
          {tickets.filter((ticket) => !OPEN_STATUSES.includes((ticket.status || '').toLowerCase())).length > 0 && (
            <div className="border-t p-4">
              <details>
                <summary className="text-sm font-semibold text-gray-700 cursor-pointer">
                  Tickets cerrados ({tickets.filter((ticket) => !OPEN_STATUSES.includes((ticket.status || '').toLowerCase())).length})
                </summary>
                <ul className="mt-3 divide-y divide-gray-100 max-h-48 overflow-y-auto">
                  {tickets
                    .filter((ticket) => !OPEN_STATUSES.includes((ticket.status || '').toLowerCase()))
                    .map((ticket) => (
                      <li
                        key={ticket.id}
                        className={`px-4 py-3 cursor-pointer ${
                          selectedTicket?.id === ticket.id ? 'bg-orange-50' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => openTicket(ticket)}
                      >
                        <div className="flex items-center justify-between">
                          <p className="font-semibold text-gray-900">{ticket.title}</p>
                          <span className="text-xs text-gray-500">
                            {(ticket.status || '').toUpperCase()}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">{ticket.clientName || ticket.clientEmail}</p>
                      </li>
                    ))}
                </ul>
              </details>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow border p-4">
          {selectedTicket ? (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase text-gray-500">Ticket</p>
                  <h4 className="text-lg font-semibold text-gray-900">{selectedTicket.title}</h4>
                </div>
                <select
                  className="border rounded px-3 py-2 text-sm"
                  value={selectedTicket.status}
                  onChange={(e) => changeStatus(e.target.value)}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-4">
                <h5 className="text-sm font-semibold text-gray-700 mb-2">Actualizaciones</h5>
                {timeline.length === 0 ? (
                  <p className="text-sm text-gray-500">Sin notas todavía.</p>
                ) : (
                  <ul className="space-y-3 max-h-60 overflow-y-auto">
                    {timeline.map((entry) => (
                      <li key={entry.id} className="border rounded px-3 py-2 text-sm">
                        <p className="text-gray-800">{entry.summary}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {entry.createdBy || entry.channel} ·{' '}
                          {formatDateTime(entry.createdAt)}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="mt-4 space-y-2">
                <textarea
                  rows={3}
                  className="border rounded w-full px-3 py-2 text-sm"
                  placeholder="Añade una nota"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <button
                  type="button"
                  onClick={addEntry}
                  className="bg-blue-600 text-white px-4 py-2 rounded text-sm font-medium"
                >
                  Registrar nota
                </button>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">Selecciona un ticket para ver los detalles.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminClientService;
